'use client';

import { useState } from 'react';
import { trim as _trim } from 'lodash-es';
import { Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import TextField from '../forms/text-field';
import TextAreaField from '../forms/text-area-field';
import type { Type } from '../../types/Event/Type';
import { combineDateAndTime } from '../../utils/time';
import {
  getEventCategoryIds,
} from '../../utils/event-categories';
import { useAppContext } from '../../contexts/app';
import type { Event } from '../../types/Event';
import EventTypeSelector from './event-type-selector';
import FormLocation from './form-location';
import FormImage from './form-image';
import FormPaces from './form-paces';
import FormDistances from './form-distances';
import FormTerrains from './form-terrains';
import FormRegroups from './form-regroups';
import FormRegistration from './form-registration';
import FormTime from './form-time';
import FormRouteUrl from './form-route-url';
import FormAudience from './form-audience';

const useStyles = makeStyles()((theme) => ({
  copyContactButton: {
    margin: theme.spacing(0, 0, 2, 0),
  },
}));

export type EventFormValues = {
  hostType: string;
  groupId: string;
  eventType: Type;
  name: string;
  visibility: string;
  description: string;
  startDate: Date | null;
  startTime: Date | null;
  startAtDetails: string;
  endDate: Date | null;
  endTime: Date | null;
  startLocation: string;
  startLocationAddress: string;
  startLocationDetails: string;
  loop: boolean;
  pace: string;
  distance: string;
  regroup: string;
  terrain: string;
  weatherCancellation: string;
  audienceRestriction: string;
  ageRestriction: string;
  endLocation: string;
  endLocationAddress: string;
  endLocationDetails: string;
  infoUrl: string;
  routeUrl: string;
  image: unknown;
  categoryIds: string[];
  approvalPrompt: string;
  approvalDescription: string;
  contactName: string;
  contactPhoneNumber: string;
  contactEmail: string;
  currentImage: { filename: string } | null;
  status: string;
  registrationType: string;
};

// Shared field definitions used by both draft and publish schemas
const baseEventFields = {
  hostType: Yup.string()
    .oneOf(['User', 'Group'])
    .required(),
  groupId: Yup.string().nullable(),
  eventType: Yup.string()
    .oneOf(['RIDE', 'EVENT', 'ONLINE_EVENT'])
    .required('Event type is required'),
  name: Yup.string().required('Title is required'),
  visibility: Yup
    .string()
    .oneOf(['PUBLIC', 'UNLISTED', 'PRIVATE', 'GROUP'])
    .required('Visibility is required'),
  description: Yup.string().required('Description is required'),
  startAtDetails: Yup.string().nullable(),
  endDate: Yup
    .date()
    .typeError('End date must be a date with format MM/DD/YYYY')
    .nullable(),
  endTime: Yup
    .date()
    .typeError('End time must be a time with format HH:MM AM/PM')
    .nullable(),
  startLocationAddress: Yup.string().nullable(),
  startLocationDetails: Yup.string().nullable(),
  loop: Yup.boolean(),
  pace: Yup.string().nullable(),
  distance: Yup.string().nullable(),
  regroup: Yup.string().nullable(),
  terrain: Yup.string().nullable(),
  weatherCancellation: Yup.string().nullable(),
  audienceRestriction: Yup.string().nullable(),
  ageRestriction: Yup
    .string()
    .nullable()
    .oneOf(['GENERAL', 'FAMILY', '21_PLUS'])
    .required('Age Restriction is required'),
  endLocation: Yup.string().nullable(),
  endLocationAddress: Yup.string().nullable(),
  endLocationDetails: Yup.string().nullable(),
  infoUrl: Yup
    .string()
    .nullable()
    .test('is-url', 'Info URL must be a valid URL (e.g., https://example.com/register)', (value) => {
      if (!value || !_trim(value)) { return true; }
      try {
        const url = new URL(_trim(value));
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }),
  routeUrl: Yup
    .string()
    .nullable()
    .test('is-url', 'Route URL must be a valid URL (e.g., https://ridewithgps.com/routes/123)', (value) => {
      if (!value || !_trim(value)) { return true; }
      try {
        const url = new URL(_trim(value));
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }),
  image: Yup.mixed().nullable(),
  categoryIds: Yup.array()
    .of(Yup.string().oneOf(getEventCategoryIds()))
    .ensure(),
  approvalPrompt: Yup.string().nullable(),
  approvalDescription: Yup.string().nullable(),
  contactName: Yup.string().nullable(),
  contactPhoneNumber: Yup.string().nullable(),
  contactEmail: Yup.string().nullable(),
  currentImage: Yup.object().shape({
    filename: Yup.string()
  }).nullable(),
  status: Yup
    .string()
    .oneOf(['DRAFT', 'PUBLISHED', 'GROUP_PENDING'])
    .required('Status is required'),
  registrationType: Yup
    .string()
    .oneOf(['OPEN', 'EXTERNAL_REGISTRATION', 'APPROVAL_REQUIRED'])
    .required('Registration Type is required'),
};

// Shared cross-field tests — use `unknown` for values to avoid Yup's inferred type vs EventFormValues mismatch
function testEndAfterStart(this: Yup.TestContext, values: unknown) {
  const v = values as EventFormValues;
  const startAt = combineDateAndTime(v.startDate, v.startTime);
  const endAt = combineDateAndTime(v.endDate, v.endTime);
  if (startAt && endAt && startAt.isValid() && endAt.isValid() && endAt.isBefore(startAt)) {
    return this.createError({
      path: 'endDate',
      message: 'End time must be after start time',
    });
  }
  return true;
}

function testHostGroupSelected(this: Yup.TestContext, values: unknown) {
  const { hostType, groupId } = values as EventFormValues;
  if (hostType === 'Group' && !groupId) {
    return this.createError({
      path: 'groupId',
      message: 'Group must be selected if host is a group.',
    });
  }
  return true;
}

function testPrivacyGroupSelected(this: Yup.TestContext, values: unknown) {
  const { visibility, groupId } = values as EventFormValues;
  if (visibility === 'GROUP' && !groupId) {
    return this.createError({
      path: 'groupId',
      message: 'Group must be selected if privacy is group.',
    });
  }
  return true;
}

// Draft schema — startDate/startTime are optional and location is not enforced
export const draftEventSchema = Yup.object().shape({
  ...baseEventFields,
  startDate: Yup
    .date()
    .typeError('Start date must be a date with format MM/DD/YYYY')
    .nullable(),
  startTime: Yup
    .date()
    .typeError('Start time must be a time with format HH:MM AM/PM')
    .nullable(),
  startLocation: Yup.string().nullable(),
}).test('end-after-start', testEndAfterStart)
  .test('host-group-selected', testHostGroupSelected)
  .test('privacy-group-selected', testPrivacyGroupSelected);

// Publish schema — full validation for publishing and editing published events
export const publishEventSchema = Yup.object().shape({
  ...baseEventFields,
  startDate: Yup
    .date()
    .typeError('Start date must be a date with format MM/DD/YYYY')
    .nullable()
    .required('Start date is required'),
  startTime: Yup
    .date()
    .typeError('Start time must be a time with format HH:MM AM/PM')
    .nullable()
    .required('Start time is required'),
  startLocation: Yup.string().nullable(),
}).test('end-after-start', testEndAfterStart)
  .test('host-group-selected', testHostGroupSelected)
  .test('privacy-group-selected', testPrivacyGroupSelected)
  .test('published-has-location', function testPublishedHasLocation(values) {
    const { status, startLocation, eventType } = values;
    if (eventType !== 'ONLINE_EVENT' && status === 'PUBLISHED' && (!startLocation || !_trim(startLocation))) {
      return this.createError({
        path: 'startLocation',
        message: 'Start location is required when publishing.',
      });
    }
    return true;
  });

type Props = {
  disabled?: boolean;
  event?: Event;
  isEventSeries?: boolean;
  name?: string;
  currentImage?: { filename: string } | null;
};

const EventForm = (props: Props) => {
  const {
    event,
    disabled: formDisabled,
    isEventSeries = false,
  } = props;

  const { values: { eventType }, isSubmitting, submitCount, setFieldValue } = useFormikContext<EventFormValues>();

  const { loggedInUser } = useAppContext();

  const { classes } = useStyles();
  const disabled = formDisabled || isSubmitting;
  const forceTouched = submitCount > 0;

  const [contactOpen, setContactOpen] = useState(!event);

  return (
    <Form>
      {/* The Basics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2">The Basics</Typography>
        <EventTypeSelector disabled={disabled} />
        <TextField
          name="name"
          label="Title"
          disabled={disabled}
          touched={forceTouched}
        />
        <TextAreaField
          name="description"
          label="Description"
          disabled={disabled}
          touched={forceTouched}
          helperText={(
            <span>
              <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                Markdown
              </MLink>
              {' '}is supported
            </span>
          )}
          minRows={6}
        />
        <FormImage
          disabled={disabled}
          forceTouched={forceTouched}
        />
      </Paper>

      {/* When */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormTime
          disabled={disabled}
          forceTouched={forceTouched}
        />
      </Paper>

      {/* Where */}
      {eventType !== 'ONLINE_EVENT' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <FormLocation
            disabled={disabled}
            forceTouched={forceTouched}
          />
        </Paper>
      )}

      {/* Ride Details (RIDE only) */}
      {eventType === 'RIDE' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h2">Ride Details</Typography>
          <FormRouteUrl
            disabled={disabled}
            touched={forceTouched}
          />
          <FormPaces
            disabled={disabled}
            touched={forceTouched}
            sx={{ my: 2 }}
          />
          <FormDistances
            disabled={disabled}
            touched={forceTouched}
            sx={{ my: 2 }}
          />
          <FormTerrains
            disabled={disabled}
            touched={forceTouched}
            sx={{ my: 2 }}
          />
          <FormRegroups
            disabled={disabled}
            touched={forceTouched}
            sx={{ my: 2 }}
          />
        </Paper>
      )}

      {/* Audience & Visibility */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormAudience
          disabled={disabled}
          forceTouched={forceTouched}
          event={event}
          isEventSeries={isEventSeries}
        />
      </Paper>

      {/* Registration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormRegistration
          disabled={disabled}
          forceTouched={forceTouched}
        />
        <TextField
          name="infoUrl"
          label="Info URL (optional)"
          helperText="Registration page or primary event URL"
          disabled={disabled}
          touched={forceTouched}
        />
      </Paper>

      {/* Contact Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h2"
          onClick={() => setContactOpen(!contactOpen)}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', userSelect: 'none' }}
        >
          Contact Information
          {contactOpen ? <ExpandLess sx={{ ml: 1 }} /> : <ExpandMore sx={{ ml: 1 }} />}
        </Typography>
        <Collapse in={contactOpen}>
          <p>
            Contact information is shared with everyone who has access to the event.
          </p>
          <Button
            onClick={() => {
              setFieldValue('contactName', loggedInUser.displayName);
              setFieldValue('contactEmail', loggedInUser.email);
              setFieldValue('contactPhoneNumber', loggedInUser.phoneNumberDisplay);
            }}
            size="small"
            variant="contained"
            className={classes.copyContactButton}
            disabled={disabled}
          >
            Copy my contact information
          </Button>
          <TextField
            name="contactName"
            label="Contact name (optional)"
            disabled={disabled}
            touched={forceTouched}
          />
          <TextField
            name="contactEmail"
            label="Contact email (optional)"
            disabled={disabled}
            touched={forceTouched}
          />
          <TextField
            name="contactPhoneNumber"
            label="Contact phone number (optional)"
            disabled={disabled}
            touched={forceTouched}
          />
        </Collapse>
      </Paper>

    </Form>
  );
};

export default EventForm;
